export const DEFAULT_GTM_CONSENT_WAIT_FOR_UPDATE = 1500;
export const GTM_CONSENT_UPDATE_RETRY_DELAY_MS = 50;
export const GTM_CONSENT_UPDATE_RETRY_BUFFER_ATTEMPTS = 2;
// Keep at least a 1000ms replay window for Cookiebot updates that arrive before
// GTM creates window.gtag, even when wait_for_update is configured lower.
export const GTM_CONSENT_UPDATE_MIN_ATTEMPTS = 20;

type CookiebotConsentState = {
  marketing?: boolean;
  preferences?: boolean;
  statistics?: boolean;
};

type ConsentValue = "granted" | "denied";

export type GtmConsentUpdate = {
  ad_storage: ConsentValue;
  ad_user_data: ConsentValue;
  ad_personalization: ConsentValue;
  analytics_storage: ConsentValue;
  functionality_storage: ConsentValue;
  personalization_storage: ConsentValue;
};

const toConsentValue = (value: boolean): ConsentValue =>
  value ? "granted" : "denied";

export const parseGtmConsentWaitForUpdate = (
  value: string | undefined,
): number => {
  if (!value) {
    return DEFAULT_GTM_CONSENT_WAIT_FOR_UPDATE;
  }

  const parsedValue = Number(value);

  return Number.isFinite(parsedValue) && parsedValue >= 0
    ? parsedValue
    : DEFAULT_GTM_CONSENT_WAIT_FOR_UPDATE;
};

export const getGtmConsentUpdateMaxAttempts = (
  waitForUpdateMs: number,
): number =>
  Math.max(
    GTM_CONSENT_UPDATE_MIN_ATTEMPTS,
    Math.ceil(waitForUpdateMs / GTM_CONSENT_UPDATE_RETRY_DELAY_MS) +
      GTM_CONSENT_UPDATE_RETRY_BUFFER_ATTEMPTS,
  );

export const shouldRetryGtmConsentUpdate = (
  attempt: number,
  maxAttempts: number,
): boolean => attempt < maxAttempts;

export const runGtmConsentCleanupThenUpdate = (
  clearRevokedStorage: () => void,
  updateConsent: () => void,
): void => {
  try {
    clearRevokedStorage();
  } catch {
    // Consent updates must continue when browser storage access is blocked.
  }

  updateConsent();
};

export const buildGtmConsentUpdate = (
  consent: CookiebotConsentState | undefined,
): GtmConsentUpdate => {
  const {
    marketing = false,
    statistics = false,
    preferences = false,
  } = consent ?? {};

  return {
    ad_storage: toConsentValue(Boolean(marketing)),
    ad_user_data: toConsentValue(Boolean(marketing)),
    ad_personalization: toConsentValue(Boolean(marketing)),
    analytics_storage: toConsentValue(Boolean(statistics)),
    functionality_storage: toConsentValue(Boolean(preferences)),
    personalization_storage: toConsentValue(Boolean(preferences || marketing)),
  };
};
